import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * Capacitor Compatibility Helper
 * Ensures all features work seamlessly on both web and native mobile platforms
 */

export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

export const getPlatform = () => {
  return Capacitor.getPlatform(); // 'web', 'ios', 'android'
};

/**
 * App State Management for Capacitor
 * Helps manage polling/intervals when app is backgrounded
 */
let appStateListeners = [];
let isAppActive = true;

export const initializeAppStateListener = () => {
  if (!isNativePlatform()) return;

  // Listen to app state changes
  App.addListener('appStateChange', (state) => {
    isAppActive = state.isActive;
    
    // Notify all listeners
    appStateListeners.forEach(callback => {
      callback(state.isActive);
    });
  });
};

export const addAppStateListener = (callback) => {
  appStateListeners.push(callback);
  
  // Return cleanup function
  return () => {
    appStateListeners = appStateListeners.filter(cb => cb !== callback);
  };
};

export const isAppActiveState = () => {
  return isAppActive;
};

/**
 * Network Status for Capacitor
 * Helps handle offline scenarios
 */
export const checkNetworkStatus = async () => {
  if (!isNativePlatform()) {
    return navigator.onLine;
  }

  try {
    const { Network } = await import('@capacitor/network');
    const status = await Network.getStatus();
    return status.connected;
  } catch (error) {
    console.error('Network status check error:', error);
    return true; // Assume online if check fails
  }
};

/**
 * Safe Polling with App State Awareness
 * Automatically pauses polling when app is backgrounded
 */
export class CapacitorSafePoller {
  constructor(callback, interval) {
    this.callback = callback;
    this.interval = interval;
    this.intervalId = null;
    this.isRunning = false;
    this.appStateCleanup = null;
  }

  start() {
    if (this.isRunning) return;

    this.isRunning = true;
    
    // Set up app state listener for native apps
    if (isNativePlatform()) {
      this.appStateCleanup = addAppStateListener((isActive) => {
        if (isActive && !this.intervalId) {
          // Resume polling when app becomes active
          this.intervalId = setInterval(this.callback, this.interval);
        } else if (!isActive && this.intervalId) {
          // Pause polling when app is backgrounded
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      });
    }

    // Start polling
    this.intervalId = setInterval(this.callback, this.interval);
  }

  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.appStateCleanup) {
      this.appStateCleanup();
      this.appStateCleanup = null;
    }
  }
}

/**
 * Phone Call Handler - Works on both web and native
 */
export const makePhoneCall = (phoneNumber) => {
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  
  if (isNativePlatform()) {
    // On native, open phone dialer
    window.location.href = `tel:${cleanNumber}`;
  } else {
    // On web, open tel: link
    window.open(`tel:${cleanNumber}`, '_self');
  }
};

/**
 * SMS Handler - Works on both web and native
 */
export const sendSMS = (phoneNumber, message = '') => {
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  const encodedMessage = encodeURIComponent(message);
  
  if (isNativePlatform()) {
    window.location.href = `sms:${cleanNumber}${message ? `?body=${encodedMessage}` : ''}`;
  } else {
    window.open(`sms:${cleanNumber}${message ? `?body=${encodedMessage}` : ''}`, '_self');
  }
};

/**
 * Haptic Feedback for native apps
 */
export const triggerHapticFeedback = async (style = 'medium') => {
  if (!isNativePlatform()) return;

  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    }[style] || ImpactStyle.Medium;

    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('Haptic feedback error:', error);
  }
};

/**
 * Safe fetch with network check
 */
export const capacitorSafeFetch = async (url, options = {}) => {
  // Check network status on native
  if (isNativePlatform()) {
    const isOnline = await checkNetworkStatus();
    if (!isOnline) {
      throw new Error('No internet connection');
    }
  }

  return fetch(url, options);
};

/**
 * Local notification support (optional)
 */
export const showNotification = async (title, body) => {
  if (!isNativePlatform()) {
    // Web notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
    return;
  }

  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: Date.now(),
          schedule: { at: new Date(Date.now() + 1000) }
        }
      ]
    });
  } catch (error) {
    console.error('Notification error:', error);
  }
};

/**
 * Initialize all Capacitor compatibility features
 */
export const initializeCapacitorCompatibility = () => {
  initializeAppStateListener();
  
  console.log('✅ Capacitor Compatibility initialized', {
    platform: getPlatform(),
    isNative: isNativePlatform()
  });
};

export default {
  isNativePlatform,
  getPlatform,
  initializeAppStateListener,
  addAppStateListener,
  isAppActiveState,
  checkNetworkStatus,
  CapacitorSafePoller,
  makePhoneCall,
  sendSMS,
  triggerHapticFeedback,
  capacitorSafeFetch,
  showNotification,
  initializeCapacitorCompatibility
};
